const BUCKET_MEDIA =
    process.env.MEDIA_BUCKET_ID
    ?? (typeof import.meta !== 'undefined'
        ? (import.meta.env?.VITE_MEDIA_BUCKET_ID as string | undefined)
        : undefined)
    ?? 'media';

const BUCKET_ADS =
    process.env.ADS_BUCKET_ID
    ?? (typeof import.meta !== 'undefined'
        ? (import.meta.env?.VITE_ADS_BUCKET_ID as string | undefined)
        : undefined)
    ?? 'ads';

const BUCKET_TIPS =
    process.env.TIPS_BUCKET_ID
    ?? (typeof import.meta !== 'undefined'
        ? (import.meta.env?.VITE_TIPS_BUCKET_ID as string | undefined)
        : undefined)
    ?? 'tips';

function viteEnv(key: string): string | undefined {
    if (typeof import.meta === 'undefined') return undefined;
    const value = import.meta.env?.[key];
    return typeof value === 'string' && value ? value : undefined;
}

export function appwriteOrigin(): string {
    try {
        return new URL(endpoint()).origin;
    } catch {
        return '';
    }
}

function endpoint(): string {
    return (
        process.env.APPWRITE_ENDPOINT
        ?? process.env.VITE_APPWRITE_ENDPOINT
        ?? viteEnv('VITE_APPWRITE_ENDPOINT')
        ?? 'https://cloud.appwrite.io/v1'
    );
}

function project(): string {
    return (
        process.env.APPWRITE_PROJECT_ID
        ?? process.env.VITE_APPWRITE_PROJECT_ID
        ?? viteEnv('VITE_APPWRITE_PROJECT_ID')
        ?? ''
    );
}

export type FilePreviewOutput = 'avif' | 'webp' | 'jpg' | 'jpeg' | 'png' | 'gif' | 'heic';

export interface FilePreviewOptions {
    width?: number;
    height?: number;
    gravity?: string;
    quality?: number;
    output?: FilePreviewOutput;
}

function clampInt(n: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, Math.round(n)));
}

/** Content / article media bucket id. */
export function mediaBucketId(): string {
    return BUCKET_MEDIA;
}

/** Display-ad creatives bucket id. */
export function adsBucketId(): string {
    return BUCKET_ADS;
}

/** Private reader-tip photo bucket (API key only, not public). */
export function tipsBucketId(): string {
    return BUCKET_TIPS;
}

export function filePreviewUrl(
    fileId: string | undefined | null,
    opts: FilePreviewOptions = {},
    bucketId = BUCKET_MEDIA,
): string | undefined {
    if (!fileId) return undefined;
    const proj = project();
    if (!proj) return undefined;

    const params = new URLSearchParams({ project: proj });
    if (opts.width != null) params.set('width', String(clampInt(opts.width, 0, 4000)));
    if (opts.height != null) params.set('height', String(clampInt(opts.height, 0, 4000)));
    params.set('gravity', opts.gravity ?? 'auto');
    if (opts.quality != null) params.set('quality', String(clampInt(opts.quality, 0, 100)));
    params.set('output', opts.output ?? 'avif');

    return `${endpoint()}/storage/buckets/${bucketId}/files/${encodeURIComponent(fileId)}/preview?${params}`;
}

/** Preview URL for a file in the ads creatives bucket. */
export function adFilePreviewUrl(
    fileId: string | undefined | null,
    opts: FilePreviewOptions = {},
): string | undefined {
    return filePreviewUrl(fileId, opts, BUCKET_ADS);
}

export function appwriteViewHrefToPreview(href: string, opts: FilePreviewOptions): string {
    const m = href.match(/\/storage\/buckets\/([^/]+)\/files\/([^/?#]+)\/view(?:\?|$|#)/);
    if (!m) return href;
    const bucketId = m[1];
    const fileId = decodeURIComponent(m[2]);
    return filePreviewUrl(fileId, opts, bucketId) ?? href;
}

export function isAppwriteFilePreviewUrl(url: string): boolean {
    return /\/storage\/buckets\/[^/]+\/files\/[^/]+\/preview/.test(url);
}

const SRCSET_WIDTHS = [320, 480, 640, 800, 1100, 1200, 1600];

/** Same Appwrite preview with a smaller width (and proportional height). */
export function previewUrlWithWidth(src: string, width: number): string {
    if (!isAppwriteFilePreviewUrl(src)) return src;
    try {
        const url = new URL(src);
        const origW = Number(url.searchParams.get('width') || 0);
        const origH = Number(url.searchParams.get('height') || 0);
        const maxW = origW > 0 ? origW : 4000;
        const nextW = clampInt(width, 1, maxW);
        url.searchParams.set('width', String(nextW));
        if (origW > 0 && origH > 0) {
            url.searchParams.set(
                'height',
                String(Math.max(1, Math.round((origH * nextW) / origW))),
            );
        }
        return url.toString();
    } catch {
        return src;
    }
}

/** `srcset` candidates up to the preview's requested width. */
export function previewSrcSet(src: string | undefined | null): string | undefined {
    if (!src || !isAppwriteFilePreviewUrl(src)) return undefined;
    try {
        const url = new URL(src);
        const origW = Number(url.searchParams.get('width') || 0);
        if (!origW) return undefined;
        const widths = SRCSET_WIDTHS.filter((w) => w < origW);
        widths.push(origW);
        return [...new Set(widths)]
            .map((w) => `${previewUrlWithWidth(src, w)} ${w}w`)
            .join(', ');
    } catch {
        return undefined;
    }
}
